import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { MemberOption } from './member-multiselect-input.interfaces';
import { MemberSelectPanelComponent } from '../member-select-panel/member-select-panel.component';
import { AvatarComponent } from '../avatar/avatar.component';
import { DropdownCoordinationService } from '../dropdown-coordination.service';

@Component({
  selector: 'ocp-member-multiselect-input',
  imports: [HlmPopoverImports, AvatarComponent, MemberSelectPanelComponent, ThemedIconComponent],
  templateUrl: './member-multiselect-input.component.html',
  styleUrl: './member-multiselect-input.component.scss',
  host: { class: 'member-multiselect-input' },
})
export class MemberMultiselectInputComponent {
  private readonly dropdownCoordination = inject(DropdownCoordinationService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  /** Currently selected member IDs */
  readonly value = input<string[]>([]);

  /** Available members to select from */
  readonly options = input<MemberOption[]>([]);

  /** Placeholder text when no members selected */
  readonly placeholder = input<string>('');

  /** Accessible label */
  readonly ariaLabel = input<string>('');

  /** Popover width strategy: 'match-trigger-width' syncs to trigger width, or a CSS value sets max-width. */
  readonly popoverWidth = input<string | undefined>(undefined);

  /** Emits updated selection array after each toggle */
  readonly membersChange = output<string[]>();

  /** Emits when the multiselect popover is opened */
  readonly editorOpen = output<void>();

  /** Emits when the multiselect popover is closed */
  readonly editorClose = output<void>();

  private readonly panel = viewChild<MemberSelectPanelComponent>('panel');
  private resizeListener: (() => void) | null = null;

  protected readonly isActive = signal(false);
  protected readonly overlayState = signal<'open' | 'closed' | null>(null);
  protected readonly triggerWidth = signal<string>('auto');

  /** Local selection state — synced from input, updated immediately on toggle */
  protected readonly selectedIds = signal<string[]>([]);

  protected readonly selectedOptions = computed(() => {
    const ids = new Set(this.selectedIds());
    return this.options().filter(o => ids.has(o.value));
  });

  constructor() {
    effect(() => {
      this.selectedIds.set(this.value());
    });

    this.dropdownCoordination.registerCloseHandler(
      this.elementRef,
      () => this.closePopover(),
      this.destroyRef
    );
  }

  protected onTriggerClick(): void {
    this.dropdownCoordination.notifyOpen(this.elementRef);
    this.overlayState.set('open');
  }

  protected onStateChanged(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.overlayState.set(null);
    }
    this.isActive.set(state === 'open');
    if (state === 'open') {
      const triggerWidth = this.trigger()?.nativeElement.offsetWidth;
      this.triggerWidth.set(triggerWidth ? `${triggerWidth}px` : 'auto');
      this.keepTriggerVisible();
      this.attachViewportListener();
      this.editorOpen.emit();
      this.panel()?.reset();
      this.panel()?.focusSearch();
    } else {
      this.detachViewportListener();
      this.panel()?.reset();
      this.editorClose.emit();
    }
  }

  protected onMemberToggled(memberId: string): void {
    const current = this.selectedIds();
    const isSelected = current.includes(memberId);
    const updated = isSelected ? current.filter(id => id !== memberId) : [...current, memberId];
    this.selectedIds.set(updated);
    this.membersChange.emit(updated);
    this.closePopover();
  }

  protected removeChip(option: MemberOption, event: Event): void {
    event.stopPropagation();
    const updated = this.selectedIds().filter(id => id !== option.value);
    this.selectedIds.set(updated);
    this.membersChange.emit(updated);
  }

  protected closePopover(): void {
    this.overlayState.set('closed');
  }

  protected trackByValue(_index: number, option: MemberOption): string {
    return option.value;
  }

  protected truncateLabel(label: string, limit = 25): string {
    return label.length > limit ? `${label.substring(0, limit)}...` : label;
  }

  private attachViewportListener(): void {
    if (this.resizeListener) {
      return;
    }

    this.resizeListener = () => {
      if (!this.isActive()) {
        return;
      }

      this.keepTriggerVisible();
    };

    window.addEventListener('resize', this.resizeListener);
    this.destroyRef.onDestroy(() => this.detachViewportListener());
  }

  private detachViewportListener(): void {
    if (!this.resizeListener) {
      return;
    }

    window.removeEventListener('resize', this.resizeListener);
    this.resizeListener = null;
  }

  private keepTriggerVisible(): void {
    requestAnimationFrame(() => {
      const trigger = this.trigger()?.nativeElement;
      if (typeof trigger?.scrollIntoView === 'function') {
        trigger.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });
  }
}
